import {HIGHLIGHT_NAME_BY_COLOR, UNDERLINE_HIGHLIGHT_NAME} from './annotationConstants';
import type {Annotation, AnnotationAnchor, AnnotationRange, TextNodeMap} from './annotationTypes';

function highlightNameForAnnotation(annotation: Annotation): string {
  return annotation.type === 'underline'
    ? UNDERLINE_HIGHLIGHT_NAME
    : HIGHLIGHT_NAME_BY_COLOR[annotation.color];
}

// Skip invisible text (e.g. MathJax's `<script type="math/tex">` source)
// so it can't shift annotation offsets.
const SKIPPED_ANCESTOR_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT']);

export function buildTextNodeMap(container: Element): TextNodeMap {
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      let parent = node.parentElement;
      while (parent && parent !== container) {
        if (SKIPPED_ANCESTOR_TAGS.has(parent.tagName)) {
          return NodeFilter.FILTER_REJECT;
        }
        parent = parent.parentElement;
      }
      return NodeFilter.FILTER_ACCEPT;
    }
  });

  const map: TextNodeMap = [];
  let offset = 0;
  let current = walker.nextNode();
  while (current) {
    const node = current as Text;
    map.push({node, nodeStart: offset, nodeEnd: offset + node.length});
    offset += node.length;
    current = walker.nextNode();
  }
  return map;
}

/**
 * Maps a Range boundary to a text offset. Boundaries not on a mapped node —
 * element boundaries (triple-click) or nodes outside the container — resolve to
 * the next mapped node's start, which also clamps out-of-container endpoints inward.
 */
function resolveBoundaryOffset(
  boundaryNode: Node,
  boundaryOffset: number,
  map: TextNodeMap
): number {
  const directEntry = map.find((entry) => entry.node === boundaryNode);
  if (directEntry) {
    return directEntry.nodeStart + Math.min(boundaryOffset, directEntry.node.length);
  }

  const boundaryPoint = document.createRange();
  boundaryPoint.setStart(boundaryNode, boundaryOffset);
  boundaryPoint.collapse(true);
  const entryAtOrAfterBoundary = map.find(
    (entry) => boundaryPoint.comparePoint(entry.node, 0) !== -1
  );
  if (entryAtOrAfterBoundary) {
    return entryAtOrAfterBoundary.nodeStart;
  }
  return map.length > 0 ? map[map.length - 1].nodeEnd : 0;
}

/**
 * Converts a DOM Range to character offsets in the mapped text, clamping
 * endpoints to its bounds. Returns null when the range covers none of it.
 */
export function rangeToOffsets(range: Range, map: TextNodeMap): AnnotationRange | null {
  if (map.length === 0) {
    return null;
  }
  const start = resolveBoundaryOffset(range.startContainer, range.startOffset, map);
  const end = resolveBoundaryOffset(range.endContainer, range.endOffset, map);
  if (start >= end) {
    return null;
  }
  return {start, end};
}

/**
 * Inverse of `rangeToOffsets`. Returns null when the offsets fall outside the
 * mapped text (e.g. the DOM changed since capture).
 */
export function offsetsToRange(textRange: AnnotationRange, map: TextNodeMap): Range | null {
  if (map.length === 0) {
    return null;
  }
  const totalLength = map[map.length - 1].nodeEnd;
  const {start, end} = textRange;
  if (start < 0 || end > totalLength || start >= end) {
    return null;
  }
  const startEntry = map.find((entry) => start < entry.nodeEnd);
  const endEntry = map.find((entry) => end <= entry.nodeEnd);
  if (!startEntry || !endEntry) {
    return null;
  }
  const range = document.createRange();
  range.setStart(startEntry.node, start - startEntry.nodeStart);
  range.setEnd(endEntry.node, end - endEntry.nodeStart);
  return range;
}

/** Annotation covering `offset`; `findLast` so the topmost (last-added) wins on overlap. */
export function findAnnotationAtOffset(
  annotations: Annotation[],
  offset: number
): Annotation | undefined {
  return annotations.findLast(
    (annotation) => offset >= annotation.start && offset < annotation.end
  );
}

// Safari <17 exposes only WebKit's `caretRangeFromPoint`; other browsers the standard API.
interface CaretDocument {
  caretPositionFromPoint?: (x: number, y: number) => {offsetNode: Node; offset: number} | null;
  caretRangeFromPoint?: (x: number, y: number) => Range | null;
}

function caretFromPoint(x: number, y: number): {node: Node; offset: number} | null {
  const doc = document as unknown as CaretDocument;
  if (typeof doc.caretPositionFromPoint === 'function') {
    const position = doc.caretPositionFromPoint(x, y);
    return position ? {node: position.offsetNode, offset: position.offset} : null;
  }
  if (typeof doc.caretRangeFromPoint === 'function') {
    const range = doc.caretRangeFromPoint(x, y);
    return range ? {node: range.startContainer, offset: range.startOffset} : null;
  }
  return null;
}

/**
 * Painted highlights (Custom Highlight API) aren't elements, so hit-test by mapping
 * the point to a text offset; rebuild the map per call as MathJax shifts offsets.
 */
export function findAnnotationAtPoint(
  container: Element,
  x: number,
  y: number,
  annotations: Annotation[]
): AnnotationAnchor | null {
  const caret = caretFromPoint(x, y);
  if (!caret || !container.contains(caret.node)) {
    return null;
  }
  const map = buildTextNodeMap(container);
  const offset = resolveBoundaryOffset(caret.node, caret.offset, map);
  const annotation = findAnnotationAtOffset(annotations, offset);
  if (!annotation) {
    return null;
  }
  const range = offsetsToRange(annotation, map);
  if (!range) {
    return null;
  }
  return {rect: range.getBoundingClientRect(), id: annotation.id};
}

/**
 * The Custom Highlight API is absent in jsdom and old browsers; without it
 * capture still works and only painting is skipped.
 */
export function isHighlightApiSupported(): boolean {
  return typeof Highlight !== 'undefined' && typeof CSS !== 'undefined' && Boolean(CSS.highlights);
}

/**
 * True when the container holds math MathJax will still rewrite — a
 * `.tex2jax_process` element without MathJax's `math/tex` output yet. MathJax
 * typesets ~250ms after mount and shifts text offsets, so callers defer the
 * first paint until it settles to avoid a flash against the stale DOM.
 */
export function hasUntypesetMath(container: Element): boolean {
  if (!window.MathJax?.Hub) {
    return false;
  }
  return Array.from(container.querySelectorAll('.tex2jax_process')).some(
    (element) => !element.querySelector('script[type^="math/tex"]')
  );
}

/**
 * Converts the current window selection to offsets within `container`. Returns
 * null when there's no usable selection (none, collapsed, or outside it).
 */
export function getSelectionOffsets(container: Element): AnnotationRange | null {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
    return null;
  }
  return rangeToOffsets(selection.getRangeAt(0), buildTextNodeMap(container));
}

/**
 * Resolves stored offsets against `container`'s current text and buckets the
 * resulting Ranges by highlight name (a color background per highlight color,
 * plus the underline style); unresolvable annotations are skipped. Each name
 * has a matching `::highlight()` rule in annotations-highlights.scss. Painting
 * the result is the coordinator's job (annotationPaintCoordinator.ts).
 */
export function resolveAnnotationRanges(
  container: Element,
  annotations: Annotation[]
): Map<string, Range[]> {
  const map = buildTextNodeMap(container);
  const rangesByName = new Map<string, Range[]>();
  annotations.forEach((annotation) => {
    const range = offsetsToRange(annotation, map);
    if (!range) {
      return;
    }
    const name = highlightNameForAnnotation(annotation);
    const ranges = rangesByName.get(name) ?? [];
    ranges.push(range);
    rangesByName.set(name, ranges);
  });
  return rangesByName;
}

/**
 * Watches `target`'s subtree. `onMutate` fires immediately on each mutation
 * batch; `onSettle` fires once mutations stop (trailing debounce). Highlight
 * Ranges are live, so a painted highlight slides as MathJax rewrites the prompt —
 * callers clear via `onMutate` and repaint the settled DOM via `onSettle`.
 */
export function observeSubtreeChanges(
  target: Element,
  handlers: {onMutate?: () => void; onSettle: () => void},
  debounceMs = 150
): () => void {
  const {onMutate, onSettle} = handlers;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const observer = new MutationObserver(() => {
    onMutate?.();
    clearTimeout(timer);
    timer = setTimeout(onSettle, debounceMs);
  });
  observer.observe(target, {
    childList: true,
    subtree: true,
    characterData: true
  });
  return () => {
    clearTimeout(timer);
    observer.disconnect();
  };
}
